import { LanguagesEnum } from '@/api/enums';
import { AnswerOptionType, ModifiedAnswerOption } from '@/client/quizzes';
import { SecurityProfileQuiz as SecurityProfileQuizType } from '@/client/security-profile';
import { LoadingStatuses } from '@/common/constants';
import { QuizAnswerOption } from '@/components/quiz/QuizAnswerOption';
import { useSubmitSecurityProfileResponses } from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import SecurityProfileSvg from '@/images/security-profile/security_profile.svg';
import { AppButton } from '@/ui/buttons';
import { ProgressHeader } from '@/ui/progress-header/ProgressHeader';
import {
  SecurityProfileContentContainer,
  SecurityProfileDescription,
  SecurityProfileIconContainer,
  SecurityProfileLayout,
  SecurityProfileTitle,
} from '@/ui/security-profile-layout';
import { getTranslatedContent } from '@/utils/helpers';
import { modifyCourseQuiz } from '@/utils/helpers/courses.helper';
import { CheckboxChangeEvent } from 'primereact/checkbox';
import { RadioButtonChangeEvent } from 'primereact/radiobutton';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface SecurityProfileQuizComponentProps {
  quiz: SecurityProfileQuizType;
  accountId: string;
  onComplete: () => void;
  onShowSummary: () => void;
  attemptLanguage?: LanguagesEnum;
  editQuestionIndex?: number;
  onCancel?: () => void;
}

interface QuestionResponse {
  selectedAnswers: AnswerOption[];
  textResponse?: string;
}

type AnswerOption = {
  id: string;
  text: string;
  isCorrect: boolean;
  type: AnswerOptionType;
};

const StyledProgressSection = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledQuestionContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledQuestionText = styled.div`
  font-size: var(--heading-medium-font-size);
  line-height: var(--heading-medium-line-height);
  font-weight: 600;
  margin-bottom: var(--default-padding);
  color: var(--black-main);
  white-space: pre-line;
`;

const StyledMultiChoiceText = styled.div`
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  margin-bottom: var(--medium-padding);
`;

const StyledNavigationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: var(--big-padding);
  margin-top: auto;
`;

export const SecurityProfileQuizComponent: React.FC<
  SecurityProfileQuizComponentProps
> = ({
  quiz,
  accountId,
  onShowSummary,
  attemptLanguage = LanguagesEnum.EN,
  editQuestionIndex,
  onCancel,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { mutateAsync: submitResponses } =
    useSubmitSecurityProfileResponses(accountId);

  const [responses, setResponses] = useState<Record<string, QuestionResponse>>(
    {},
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(
    editQuestionIndex ?? 0,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const adaptedQuiz = useMemo(() => {
    if (!quiz) return null;
    return modifyCourseQuiz(quiz);
  }, [quiz]);

  const currentQuestion = adaptedQuiz?.questions?.[currentQuestionIndex];
  const currentQuestionId = currentQuestion?.id;
  const currentResponse = responses[currentQuestionId || ''] || {
    selectedAnswers: [],
    textResponse: undefined,
  };
  const isLastQuestion =
    currentQuestionIndex + 1 >= (adaptedQuiz?.questions?.length || 0);

  useEffect(() => {
    if (!quiz.responses || !adaptedQuiz?.questions) return;

    const initialResponses: Record<string, QuestionResponse> = {};

    quiz.responses.forEach((response) => {
      const question = adaptedQuiz.questions.find(
        (q) => q.id === response.questionId,
      );
      if (!question) return;

      const selectedAnswers = response.selectedAnswerIds
        .map((answerId) => {
          const answerOption = question.answerOptions?.find(
            (ao) => ao.id === answerId,
          );
          if (!answerOption) return null;

          return {
            id: answerId,
            text: getTranslatedContent(answerOption, attemptLanguage),
            isCorrect: !!answerOption.isCorrect,
            type: answerOption.type,
          };
        })
        .filter(Boolean) as AnswerOption[];

      initialResponses[response.questionId] = {
        selectedAnswers,
        textResponse: response.textResponse,
      };
    });

    setResponses(initialResponses);
  }, []);

  const handleOptionSelect = (
    answerOption: ModifiedAnswerOption,
    e: CheckboxChangeEvent | RadioButtonChangeEvent,
  ) => {
    const questionId = currentQuestion?.id;
    if (!questionId) return;

    const isSelected = e.checked;
    const option: AnswerOption = {
      id: answerOption.id || '',
      text: getTranslatedContent(answerOption, attemptLanguage),
      isCorrect: !!answerOption.isCorrect,
      type: answerOption.type,
    };

    const isMultiChoice = currentQuestion?.type === 'multiple-choice';

    setResponses((prev) => {
      const current = prev[questionId] || {
        selectedAnswers: [],
        textResponse: undefined,
      };

      return {
        ...prev,
        [questionId]: {
          ...current,
          selectedAnswers: isMultiChoice
            ? isSelected
              ? [...current.selectedAnswers, option]
              : current.selectedAnswers.filter((opt) => opt.id !== option.id)
            : isSelected
            ? [option]
            : [],
        },
      };
    });
  };

  const handleTextChange = (value: string) => {
    const questionId = currentQuestion?.id;
    if (!questionId) return;

    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || { selectedAnswers: [] }),
        textResponse: value,
      },
    }));
  };

  const validateCurrentQuestion = (): boolean => {
    const questionId = currentQuestion?.id;
    if (!questionId) return false;

    const response = responses[questionId] || {
      selectedAnswers: [],
      textResponse: undefined,
    };

    const hasCustomInput = response.selectedAnswers.some(
      (opt) => opt.type === AnswerOptionType.CUSTOM_INPUT,
    );

    if (hasCustomInput && !response.textResponse?.trim()) {
      toast?.error(t('securityProfile.error.customInputRequired'), '');
      return false;
    }

    return true;
  };

  const handleNext = async () => {
    if (!validateCurrentQuestion()) return;

    if (isLastQuestion) {
      await handleSubmit();
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const submissionData = (adaptedQuiz?.questions || []).map((question) => {
        const questionId = question.id || '';
        const response = responses[questionId] || {
          selectedAnswers: [],
          textResponse: undefined,
        };

        const hasCustomInput = response.selectedAnswers.some(
          (opt) => opt.type === AnswerOptionType.CUSTOM_INPUT,
        );

        return {
          questionId,
          selectedAnswerIds: response.selectedAnswers.map((opt) => opt.id),
          textResponse: hasCustomInput ? response.textResponse : undefined,
        };
      });

      await submitResponses(submissionData);
      onShowSummary();
    } catch (error) {
      toast?.error(t('securityProfile.error.submitFailed'), '');
    } finally {
      setIsSubmitting(false);
    }
  };

  const leftContent = (
    <>
      <SecurityProfileIconContainer>
        <img src={SecurityProfileSvg} alt="Security Profile" />
      </SecurityProfileIconContainer>
      <SecurityProfileTitle>
        {t('securityProfile.welcome.title')}
      </SecurityProfileTitle>
      <SecurityProfileDescription>
        {t('securityProfile.welcome.description')}
      </SecurityProfileDescription>
      <SecurityProfileDescription>
        {t('securityProfile.welcome.benefits')}
      </SecurityProfileDescription>
      <SecurityProfileDescription>
        {t('securityProfile.welcome.changeAnytime')}
      </SecurityProfileDescription>
    </>
  );

  const rightContent = (
    <>
      <StyledProgressSection>
        <ProgressHeader
          className="quiz-progress-header"
          currentIndex={currentQuestionIndex}
          totalCount={adaptedQuiz?.questions?.length || 0}
        />
      </StyledProgressSection>

      <SecurityProfileContentContainer>
        <StyledQuestionContent>
          <StyledQuestionText>
            {getTranslatedContent(currentQuestion, attemptLanguage)}
          </StyledQuestionText>
          {currentQuestion?.type === 'multiple-choice' && (
            <StyledMultiChoiceText
              dangerouslySetInnerHTML={{
                __html: t('content.quiz.multiChoice.text'),
              }}
            />
          )}
          {currentQuestion?.answerOptions
            ?.filter((ao) => !ao.hidden)
            .map((answerOption: ModifiedAnswerOption) => (
              <QuizAnswerOption
                key={answerOption.id}
                answerOption={{
                  id: answerOption.id || '',
                  text: getTranslatedContent(answerOption, attemptLanguage),
                  isCorrect: !!answerOption.isCorrect,
                  type: answerOption.type,
                }}
                isMultiChoice={currentQuestion?.type === 'multiple-choice'}
                isSelected={currentResponse.selectedAnswers.some(
                  (option) => option.id === answerOption.id,
                )}
                onOptionSelect={(e) => handleOptionSelect(answerOption, e)}
                textResponse={currentResponse.textResponse || ''}
                onTextResponseChange={handleTextChange}
                placeholderText={getTranslatedContent(
                  answerOption,
                  attemptLanguage,
                  'placeholderText',
                )}
                tooltipText={getTranslatedContent(
                  answerOption,
                  attemptLanguage,
                  'tooltipText',
                )}
              />
            ))}
        </StyledQuestionContent>

        <StyledNavigationContainer>
          {editQuestionIndex !== undefined && onCancel && (
            <AppButton
              label={t('button.cancel')}
              severity="secondary"
              type="outlined"
              onClick={onCancel}
              className="mr-3"
            />
          )}
          <AppButton
            label={isLastQuestion ? t('button.submit') : t('button.next')}
            severity="success"
            icon={isLastQuestion ? 'pi pi-check' : 'pi pi-arrow-right'}
            iconPos="right"
            onClick={handleNext}
            isDisabled={
              currentResponse.selectedAnswers.length === 0 || isSubmitting
            }
            state={
              isSubmitting && isLastQuestion
                ? LoadingStatuses.LOADING
                : LoadingStatuses.IDLE
            }
          />
        </StyledNavigationContainer>
      </SecurityProfileContentContainer>
    </>
  );

  return (
    <SecurityProfileLayout
      leftContent={leftContent}
      rightContent={rightContent}
    />
  );
};

export const SecurityProfileQuiz = SecurityProfileQuizComponent;
